import React, { useState, useEffect, useMemo } from 'react';
import api from '../api/axiosConfig';
import {
  Box,
  TableContainer,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Avatar,
  Typography,
  TablePagination,
  Card,
  CardHeader,
  CardContent,
  Grid,
  TableSortLabel,
  Tooltip,
  IconButton,
  Collapse,
} from '@mui/material';
import { useTheme, useMediaQuery } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { ExpandMore, ExpandLess } from '@mui/icons-material';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import RemoveIcon from '@mui/icons-material/Remove';
import { usePlayerComparison } from '../context/PlayerComparisonContext';
import { useClubTheme } from '../contexts/ClubThemeContext';
import PlayerCompareBar from './shared/PlayerCompareBar';
import PlayerCompareDialog from './shared/PlayerCompareDialog';
import PlayerRankingChip from './shared/PlayerRankingChip';
import TruncatedLinkCell from './shared/TruncatedLinkCell';
import {
  makeHeaderSx,
  makeCellSx,
  tableContainerSx,
  tableSx,
  DESKTOP_AVATAR_SIZE,
} from './shared/tableStyles';

// --- MAPPING DE COULEURS — palette pastel harmonisée ---
const colorMapping = {
  'Verde intenso':    '#4CAF50',
  'Verde medio':      '#81C784',
  'Verde claro':      '#C8E6C9',
  'Amarillo claro':   '#FFFDE7',
  'Amarillo medio':   '#FFF59D',
  'Amarillo intenso': '#FFEE58',
  'Naranja claro':    '#FFE0B2',
  'Naranja intenso':  '#FFB74D',
  'Rojo claro':       '#EF9A9A',
  'Rojo medio':       '#E57373',
};

// --- MAPPING DE POSITIONS ---
const positionMappings = {
  Base: ['Base', 'Point Guard'],
  Escolta: ['Escolta', 'Shooting Guard', 'Guard'],
  Alero: ['Alero', 'Small Forward', 'Swingman'],
  'Ala-Pívot': ['Ala-Pívot', 'Power Forward', 'Power Forward / Center'],
  Pívot: ['Pívot', 'Center'],
};

const unifyPosition = (rawPosition) => {
  if (!rawPosition) return rawPosition;
  for (const [canonicalPos, synonyms] of Object.entries(positionMappings)) {
    if (synonyms.includes(rawPosition)) return canonicalPos;
  }
  return rawPosition;
};

// --- UNITÉS DE MESURE ---
const unitMapping = {
  altura: 'cm',
  altura_con_zapatos: 'cm',
  envergadura: 'cm',
  alcance: 'cm',
  salto_vertical_maximo: 'cm',
  salto_vertical_de_pie: 'cm',
  peso: 'kg',
  grasa_corporal: '%',
  longitud_mano: 'cm',
  ancho_mano: 'cm',
  press_banca: 'kg',
  agilidad: 's',
  sprint_cuarto: 's',
};

const formatValueWithUnit = (value, columnId) => {
  if (!value || value === '-') return '-';
  const unit = unitMapping[columnId];
  return unit ? `${value} ${unit}` : value;
};

const calculateEvolution = (currentValue, previousValue, columnId) => {
  if (!currentValue || !previousValue) return null;
  const current = parseFloat(currentValue);
  const previous = parseFloat(previousValue);
  if (isNaN(current) || isNaN(previous)) return null;

  const diff = current - previous;
  const percentChange = ((diff / previous) * 100).toFixed(1);

  // Pour certaines métriques, une diminution est positive
  const inverseMetrics = ['grasa_corporal', 'agilidad', 'sprint_cuarto'];
  const isInverse = inverseMetrics.includes(columnId);

  let trend = 'stable';
  if (Math.abs(diff) > 0.01) {
    trend = isInverse ? (diff < 0 ? 'up' : 'down') : (diff > 0 ? 'up' : 'down');
  }
  return { diff, percentChange, trend };
};

const getTrendIcon = (trend) => {
  switch (trend) {
    case 'up':
      return <TrendingUpIcon sx={{ fontSize: 14, color: '#4CAF50', ml: 0.5 }} />;
    case 'down':
      return <TrendingDownIcon sx={{ fontSize: 14, color: '#F44336', ml: 0.5 }} />;
    default:
      return <RemoveIcon sx={{ fontSize: 14, color: '#9E9E9E', ml: 0.5 }} />;
  }
};

const MedidasResults = ({
  filteredPlayers,
  page,
  rowsPerPage,
  onPageChange,
  onRowsPerPageChange,
  mediaUrl,
  timestamp,
  isMobile,
}) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));

  const { primaryColor: clubPrimaryColor } = useClubTheme();
  const headerSx = makeHeaderSx(clubPrimaryColor);
  const cellSx = makeCellSx;

  // Comparaison
  const {
    compareMode,
    playersToCompare,
    originalPlayers,
    effectivePlayers,
    togglePlayerComparison,
    isPlayerSelected,
    enterCompareMode,
    exitCompareMode,
    clearComparison,
  } = usePlayerComparison();

  // Dialog comparaison
  const [compareDialogOpen, setCompareDialogOpen] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [searchResults, setSearchResults] = useState([]);

  // Expansion historique
  const [expandedPlayers, setExpandedPlayers] = useState({});
  const handleToggleExpand = (playerId) => {
    setExpandedPlayers((prev) => ({ ...prev, [playerId]: !prev[playerId] }));
  };

  const handleOpenCompareDialog = () => {
    setCompareDialogOpen(true);
    setSearchResults(originalPlayers);
  };
  const handleCloseCompareDialog = () => {
    setCompareDialogOpen(false);
    setSearchText('');
    setSearchResults(originalPlayers);
    clearComparison();
  };

  useEffect(() => {
    if (searchText.length < 3) {
      setSearchResults([]);
      return;
    }
    const lower = searchText.toLowerCase();
    setSearchResults(originalPlayers.filter((p) => p.nom.toLowerCase().includes(lower)));
  }, [searchText, originalPlayers]);

  const handleValidateCompare = () => {
    if (playersToCompare.length < 2) return;
    enterCompareMode();
    setCompareDialogOpen(false);
  };

  // Tri
  const [order, setOrder] = useState('asc');
  const [orderBy, setOrderBy] = useState('');

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const descendingComparator = (a, b, key) => {
    const getMostRecentValue = (player) => {
      if (!player.medidas || player.medidas.length === 0) return null;
      return player.medidas[0][key];
    };
    const valA = getMostRecentValue(a) ?? '';
    const valB = getMostRecentValue(b) ?? '';
    if (valB < valA) return -1;
    if (valB > valA) return 1;
    return 0;
  };
  const getComparator = (o, key) => {
    return o === 'desc'
      ? (a, b) => descendingComparator(a, b, key)
      : (a, b) => -descendingComparator(a, b, key);
  };
  const stableSort = (array, comparator) => {
    const stabilized = array.map((el, idx) => [el, idx]);
    stabilized.sort((a, b) => {
      const cmp = comparator(a[0], b[0]);
      if (cmp !== 0) return cmp;
      return a[1] - b[1];
    });
    return stabilized.map((el) => el[0]);
  };

  // Color ranges
  const [colorRanges, setColorRanges] = useState([]);
  useEffect(() => {
    const fetchColorRanges = async () => {
      try {
        const response = await api.get('/api/color-ranges');
        setColorRanges(response.data);
      } catch (err) {
        console.error('Erreur lors de la récupération des color_ranges :', err);
      }
    };
    fetchColorRanges();
  }, []);

  const getBackgroundColor = (metric, value, position) => {
    if (!value || !metric || !position) return 'inherit';
    if (colorRanges.length === 0) return 'inherit';
    const numericValue = parseFloat(value);
    if (isNaN(numericValue)) return 'inherit';
    const unifiedPos = unifyPosition(position);
    const ranges = colorRanges.filter(
      (range) => range.position === unifiedPos && range.metric === metric
    );
    for (const range of ranges) {
      const minVal = parseFloat(range.min_value);
      const maxVal = parseFloat(range.max_value);
      if (
        !isNaN(minVal) &&
        !isNaN(maxVal) &&
        numericValue >= minVal &&
        numericValue <= maxVal
      ) {
        return colorMapping[range.color_label] || 'inherit';
      }
    }
    return 'inherit';
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  };

  const sortMedidasByDate = (medidas) => {
    if (!medidas || medidas.length === 0) return [];
    return [...medidas].sort((a, b) => new Date(b.fecha) - new Date(a.fecha));
  };

  const sortedPlayers = useMemo(() => {
    if (!orderBy) return effectivePlayers;
    return stableSort(effectivePlayers, getComparator(order, orderBy));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectivePlayers, orderBy, order]);

  const totalCount = sortedPlayers.length;

  // Colonnes
  const columns = [
    { id: 'altura', label: t('medidasResults.columns.altura') },
    { id: 'envergadura', label: t('medidasResults.columns.envergadura') },
    { id: 'alcance', label: t('medidasResults.columns.alcance') },
    { id: 'salto_vertical_maximo', label: t('medidasResults.columns.saltoMax') },
    { id: 'salto_vertical_de_pie', label: t('medidasResults.columns.saltoPie') },
    { id: 'peso', label: t('medidasResults.columns.peso') },
    { id: 'grasa_corporal', label: t('medidasResults.columns.grasa') },
    { id: 'longitud_mano', label: t('medidasResults.columns.longMano') },
    { id: 'ancho_mano', label: t('medidasResults.columns.anchoMano') },
    { id: 'agilidad', label: t('medidasResults.columns.agilidad') },
    { id: 'sprint_cuarto', label: t('medidasResults.columns.sprintCuarto') },
  ];

  // -------------------------------------------------------------------------
  // ÉLÉMENTS PARTAGÉS
  // -------------------------------------------------------------------------
  const compareDialogLabels = {
    title: t('medidasResults.comparison.title'),
    searchPlaceholder: t('medidasResults.comparison.searchPlaceholder'),
    selectedSingular: t('medidasResults.comparison.selectedSingular'),
    selectedPlural: t('medidasResults.comparison.selectedPlural'),
    cancel: t('medidasResults.buttons.cancel'),
    validate: t('medidasResults.buttons.validate'),
  };

  const compareDialog = (
    <PlayerCompareDialog
      open={compareDialogOpen}
      onClose={handleCloseCompareDialog}
      onValidate={handleValidateCompare}
      searchText={searchText}
      setSearchText={setSearchText}
      searchResults={searchResults}
      playersToCompare={playersToCompare}
      togglePlayerComparison={togglePlayerComparison}
      isPlayerSelected={isPlayerSelected}
      mediaUrl={mediaUrl}
      timestamp={timestamp}
      labels={compareDialogLabels}
    />
  );

  const evolutionLegend = (
    <Box
      sx={{
        display: 'flex',
        gap: 1.5,
        alignItems: 'center',
        flexWrap: 'wrap',
        px: 0.5,
        py: 0.25,
        borderRadius: 1.5,
        backgroundColor: 'white',
        border: '1px solid #ececec',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
        <TrendingUpIcon sx={{ fontSize: 16, color: '#4CAF50' }} />
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
          {t('medidasResults.legend.mejora')}
        </Typography>
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
        <TrendingDownIcon sx={{ fontSize: 16, color: '#F44336' }} />
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
          {t('medidasResults.legend.disminucion')}
        </Typography>
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
        <RemoveIcon sx={{ fontSize: 16, color: '#9E9E9E' }} />
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
          {t('medidasResults.legend.sinCambio')}
        </Typography>
      </Box>
    </Box>
  );

  const renderCompareBar = (dense = false) => (
    <PlayerCompareBar
      count={totalCount}
      countLabel={
        totalCount !== 1
          ? t('medidasResults.counter.playerPlural')
          : t('medidasResults.counter.playerSingular')
      }
      compareMode={compareMode}
      onOpenCompare={handleOpenCompareDialog}
      onExitCompare={exitCompareMode}
      compareLabel={t('medidasResults.buttons.compare')}
      backLabel={t('medidasResults.buttons.back')}
      dense={dense}
    >
      {evolutionLegend}
    </PlayerCompareBar>
  );

  // -------------------------------------------------------------------------
  // MOBILE
  // -------------------------------------------------------------------------
  if (isMobile || isSmallScreen) {
    const mobileColumnsLabel = (id) => {
      switch (id) {
        case 'altura': return t('medidasResults.mobile.altura');
        case 'envergadura': return t('medidasResults.mobile.envergadura');
        case 'alcance': return t('medidasResults.mobile.alcance');
        case 'salto_vertical_maximo': return t('medidasResults.mobile.saltoVMax');
        case 'salto_vertical_de_pie': return t('medidasResults.mobile.saltoVPie');
        case 'peso': return t('medidasResults.mobile.peso');
        case 'grasa_corporal': return t('medidasResults.mobile.grasaCorporal');
        case 'longitud_mano': return t('medidasResults.mobile.longMano');
        case 'ancho_mano': return t('medidasResults.mobile.anchoMano');
        case 'agilidad': return t('medidasResults.mobile.agilidad');
        case 'sprint_cuarto': return t('medidasResults.mobile.sprintCuarto');
        default: return id;
      }
    };

    return (
      <Box>
        {renderCompareBar(true)}

        {sortedPlayers
          .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
          .map((player, index) => {
            const sortedMedidas = sortMedidasByDate(player.medidas);
            const mostRecentMedida = sortedMedidas.length > 0 ? sortedMedidas[0] : null;
            const hasMultipleMedidas = sortedMedidas.length > 1;
            const isExpanded = expandedPlayers[player.id];
            const globalRanking = page * rowsPerPage + index + 1;

            return (
              <Card
                key={player.id}
                sx={{
                  mb: 2,
                  borderRadius: 2,
                  boxShadow: '0 1px 3px rgba(0,0,0,0.06), 0 1px 2px rgba(0,0,0,0.04)',
                }}
              >
                <CardHeader
                  avatar={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {orderBy && <PlayerRankingChip ranking={globalRanking} />}
                      <Avatar
                        src={`${mediaUrl}/photos/${player.id}.png?${timestamp}`}
                        alt={player.nom}
                        sx={{ width: 56, height: 56 }}
                      />
                    </Box>
                  }
                  title={
                    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                      {player.nom}
                    </Typography>
                  }
                  subheader={
                    <Box>
                      <Typography variant="caption" color="textSecondary">
                        {player.poste}
                      </Typography>
                      {mostRecentMedida && (
                        <Typography variant="caption" color="textSecondary" display="block">
                          {formatDate(mostRecentMedida.fecha)} — {mostRecentMedida.fuente}
                        </Typography>
                      )}
                    </Box>
                  }
                  action={
                    hasMultipleMedidas && (
                      <IconButton onClick={() => handleToggleExpand(player.id)} size="small">
                        {isExpanded ? <ExpandLess /> : <ExpandMore />}
                      </IconButton>
                    )
                  }
                />
                <CardContent sx={{ pt: 0 }}>
                  {mostRecentMedida ? (
                    <>
                      <Grid container spacing={1}>
                        {columns.map((col) => (
                          <Grid
                            item
                            xs={6}
                            key={col.id}
                            sx={{
                              backgroundColor: getBackgroundColor(
                                col.id,
                                mostRecentMedida[col.id],
                                player.poste
                              ),
                              textAlign: 'center',
                              borderRadius: 1,
                              py: 0.5,
                            }}
                          >
                            <Typography variant="caption" sx={{ fontWeight: 700, opacity: 0.7 }}>
                              {mobileColumnsLabel(col.id)}
                            </Typography>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              {formatValueWithUnit(mostRecentMedida[col.id], col.id)}
                            </Typography>
                          </Grid>
                        ))}
                      </Grid>

                      {hasMultipleMedidas && (
                        <Collapse in={isExpanded}>
                          <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid #ddd' }}>
                            <Typography variant="subtitle2" sx={{ mb: 1 }}>
                              {t('medidasResults.mobile.historicoMedidas')}
                            </Typography>
                            {sortedMedidas.slice(1).map((medida, idx) => (
                              <Box key={idx} sx={{ mb: 2 }}>
                                <Typography variant="caption" color="textSecondary">
                                  {formatDate(medida.fecha)} — {medida.fuente}
                                </Typography>
                                <Grid container spacing={1} sx={{ mt: 0.5 }}>
                                  {columns.map((col) => {
                                    const currentValue = sortedMedidas[idx][col.id];
                                    const previousValue = sortedMedidas[idx + 1]?.[col.id];
                                    const evolution = calculateEvolution(
                                      currentValue,
                                      previousValue,
                                      col.id
                                    );
                                    return (
                                      <Grid item xs={6} key={col.id}>
                                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                          <Typography variant="caption" color="textSecondary">
                                            {col.label}: {formatValueWithUnit(currentValue, col.id)}
                                          </Typography>
                                          {evolution && (
                                            <Tooltip
                                              title={`${evolution.diff > 0 ? '+' : ''}${evolution.diff} (${evolution.percentChange}%)`}
                                            >
                                              {getTrendIcon(evolution.trend)}
                                            </Tooltip>
                                          )}
                                        </Box>
                                      </Grid>
                                    );
                                  })}
                                </Grid>
                              </Box>
                            ))}
                          </Box>
                        </Collapse>
                      )}
                    </>
                  ) : (
                    <Typography variant="body2" color="textSecondary">
                      {t('medidasResults.mobile.sinMedidas')}
                    </Typography>
                  )}
                </CardContent>
              </Card>
            );
          })}

        <TablePagination
          component="div"
          count={totalCount}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={onPageChange}
          onRowsPerPageChange={onRowsPerPageChange}
          rowsPerPageOptions={[10, 25, 50, 100]}
          labelRowsPerPage=""
          labelDisplayedRows={({ from, to, count }) =>
            t('medidasResults.pagination.displayedRows', { from, to, count })
          }
        />

        {compareDialog}
      </Box>
    );
  }

  // -------------------------------------------------------------------------
  // DESKTOP
  // -------------------------------------------------------------------------
  return (
    <Box>
      {renderCompareBar()}

      <TableContainer component={Paper} sx={tableContainerSx}>
        <Table sx={tableSx}>
          <TableHead>
            <TableRow>
              {/* Avatar + ranking */}
              <TableCell sx={{ ...headerSx(orderBy ? 75 : 50) }}>
                {/* vide */}
              </TableCell>

              {/* Bouton expand */}
              <TableCell sx={{ ...headerSx(50) }}>
                {t('medidasResults.table.history')}
              </TableCell>

              {/* Nom */}
              <TableCell sx={{ ...headerSx(140) }}>
                {t('medidasResults.table.name')}
              </TableCell>

              {/* Poste */}
              <TableCell sx={{ ...headerSx(80) }}>
                {t('medidasResults.table.position')}
              </TableCell>

              {/* Date */}
              <TableCell sx={{ ...headerSx(90) }}>
                {t('medidasResults.table.date')}
              </TableCell>

              {/* Évènement */}
              <TableCell sx={{ ...headerSx(120) }}>
                {t('medidasResults.table.event')}
              </TableCell>

              {/* Mesures */}
              {columns.map((column) => (
                <TableCell
                  key={column.id}
                  sortDirection={orderBy === column.id ? order : false}
                  sx={{ ...headerSx(70) }}
                >
                  <TableSortLabel
                    active={orderBy === column.id}
                    direction={orderBy === column.id ? order : 'asc'}
                    onClick={() => handleRequestSort(column.id)}
                    sx={{
                      fontSize: '0.72rem',
                      color: 'white !important',
                      '& .MuiTableSortLabel-icon': {
                        color: 'white !important',
                        opacity: 0.6,
                        visibility: 'visible !important',
                      },
                    }}
                  >
                    {column.label}
                  </TableSortLabel>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>

          <TableBody>
            {sortedPlayers
              .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
              .map((player, index) => {
                const sortedMedidas = sortMedidasByDate(player.medidas);
                const mostRecentMedida = sortedMedidas.length > 0 ? sortedMedidas[0] : null;
                const hasMultipleMedidas = sortedMedidas.length > 1;
                const isExpanded = expandedPlayers[player.id];
                const globalRanking = page * rowsPerPage + index + 1;

                return (
                  <React.Fragment key={player.id}>
                    <TableRow hover>
                      {/* Avatar + ranking */}
                      <TableCell sx={{ ...cellSx(orderBy ? 75 : 50), padding: '4px' }}>
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5,
                            justifyContent: 'center',
                          }}
                        >
                          {orderBy && <PlayerRankingChip ranking={globalRanking} />}
                          <Avatar
                            src={`${mediaUrl}/photos/${player.id}.png?${timestamp}`}
                            alt={player.nom}
                            sx={{ width: DESKTOP_AVATAR_SIZE, height: DESKTOP_AVATAR_SIZE }}
                          />
                        </Box>
                      </TableCell>

                      {/* Bouton expand */}
                      <TableCell sx={cellSx(50)}>
                        {hasMultipleMedidas ? (
                          <IconButton size="small" onClick={() => handleToggleExpand(player.id)}>
                            {isExpanded ? <ExpandLess /> : <ExpandMore />}
                          </IconButton>
                        ) : (
                          '-'
                        )}
                      </TableCell>

                      {/* Nom */}
                      <TableCell
                        sx={{
                          ...cellSx(140),
                          textAlign: 'left',
                          padding: '4px 6px',
                        }}
                      >
                        <TruncatedLinkCell
                          text={player.nom}
                          linkUrl={`/players/${player.id}`}
                          fontSize="0.74rem"
                        />
                      </TableCell>

                      {/* Poste */}
                      <TableCell sx={cellSx(80)}>{player.poste}</TableCell>

                      {/* Date */}
                      <TableCell sx={cellSx(90)}>
                        {mostRecentMedida ? formatDate(mostRecentMedida.fecha) : '-'}
                      </TableCell>

                      {/* Évènement */}
                      <TableCell sx={cellSx(120)}>
                        {mostRecentMedida ? mostRecentMedida.fuente : '-'}
                      </TableCell>

                      {/* Mesures */}
                      {columns.map((col) => {
                        const currentValue = mostRecentMedida?.[col.id];
                        const previousValue = sortedMedidas.length > 1
                          ? sortedMedidas[1]?.[col.id]
                          : null;
                        const evolution =
                          mostRecentMedida && previousValue
                            ? calculateEvolution(currentValue, previousValue, col.id)
                            : null;

                        return (
                          <TableCell
                            key={col.id}
                            sx={{
                              ...cellSx(70),
                              backgroundColor: mostRecentMedida
                                ? getBackgroundColor(col.id, mostRecentMedida[col.id], player.poste)
                                : 'inherit',
                            }}
                          >
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                              }}
                            >
                              {mostRecentMedida ? formatValueWithUnit(mostRecentMedida[col.id], col.id) : '-'}
                              {evolution && (
                                <Tooltip
                                  title={`${evolution.diff > 0 ? '+' : ''}${evolution.diff} (${evolution.percentChange}%)`}
                                >
                                  {getTrendIcon(evolution.trend)}
                                </Tooltip>
                              )}
                            </Box>
                          </TableCell>
                        );
                      })}
                    </TableRow>

                    {/* Historique */}
                    {hasMultipleMedidas &&
                      sortedMedidas.slice(1).map((medida, idx) => (
                        <TableRow
                          key={`${player.id}-history-${idx}`}
                          sx={{
                            display: isExpanded ? 'table-row' : 'none',
                            backgroundColor: '#f5f5f5',
                          }}
                        >
                          <TableCell sx={cellSx(orderBy ? 75 : 50)} />
                          <TableCell sx={cellSx(50)} />
                          <TableCell sx={cellSx(140)} />
                          <TableCell sx={cellSx(80)} />
                          <TableCell sx={cellSx(90)}>{formatDate(medida.fecha)}</TableCell>
                          <TableCell sx={cellSx(120)}>{medida.fuente}</TableCell>
                          {columns.map((col) => {
                            const currentValue = medida[col.id];
                            const previousValue = sortedMedidas[idx + 2]?.[col.id];
                            const evolution = calculateEvolution(currentValue, previousValue, col.id);
                            return (
                              <TableCell key={col.id} sx={cellSx(70)}>
                                <Box
                                  sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                  }}
                                >
                                  {formatValueWithUnit(currentValue, col.id)}
                                  {evolution && (
                                    <Tooltip
                                      title={`${evolution.diff > 0 ? '+' : ''}${evolution.diff} (${evolution.percentChange}%)`}
                                    >
                                      {getTrendIcon(evolution.trend)}
                                    </Tooltip>
                                  )}
                                </Box>
                              </TableCell>
                            );
                          })}
                        </TableRow>
                      ))}
                  </React.Fragment>
                );
              })}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        component="div"
        count={totalCount}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={onPageChange}
        onRowsPerPageChange={onRowsPerPageChange}
        rowsPerPageOptions={[10, 25, 50, 100]}
        labelRowsPerPage=""
        labelDisplayedRows={({ from, to, count }) =>
          t('medidasResults.pagination.displayedRows', { from, to, count })
        }
      />

      {compareDialog}
    </Box>
  );
};

export default MedidasResults;
